import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle2, ZoomIn } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getScreenshots, getScreenshotImage } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../ui/modal';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

dayjs.extend(relativeTime);

function RecentScreenshots() {
  const [screenshots, setScreenshots] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadScreenshots();
    const interval = setInterval(loadScreenshots, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadScreenshots = async () => {
    try {
      const response = await getScreenshots(6, 0);
      setScreenshots(response.data.data);
    } catch (err) {
      console.error('Failed to load screenshots:', err);
    }
  };

  const handleImageClick = (screenshot) => {
    setSelectedImage(screenshot);
    setDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent Screenshots</CardTitle>
        </CardHeader>
        <CardContent>
          {screenshots.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Screenshots will appear here (captured every 5 minutes)
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {screenshots.map((screenshot) => (
                <div key={screenshot.id}>
                  <button
                    type="button"
                    onClick={() => handleImageClick(screenshot)}
                    className={cn(
                      'group relative block w-full overflow-hidden rounded-md border transition-all hover:opacity-90 hover:shadow-md',
                      screenshot.has_pii ? 'border-2 border-destructive' : 'border-border'
                    )}
                    style={{ paddingTop: '75%' }}
                  >
                    <img
                      src={getScreenshotImage(screenshot.id)}
                      alt={`Screenshot ${screenshot.id}`}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    {screenshot.has_pii && (
                      <Badge
                        variant="destructive"
                        className="absolute top-1 right-1 text-[10px]"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        PII
                      </Badge>
                    )}
                    {!screenshot.has_pii && screenshot.risk_score === 0 && (
                      <Badge
                        variant="success"
                        className="absolute top-1 right-1 text-[10px]"
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        Safe
                      </Badge>
                    )}
                    <div className="absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded-md bg-black/60 text-white">
                      <ZoomIn className="h-3.5 w-3.5" />
                    </div>
                  </button>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {dayjs(screenshot.timestamp).fromNow()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        className="max-w-3xl"
      >
        {selectedImage && (
          <>
            <ModalHeader>
              <ModalTitle>Screenshot Details</ModalTitle>
            </ModalHeader>
            <ModalBody className="space-y-3">
              <div className="text-center">
                <img
                  src={getScreenshotImage(selectedImage.id)}
                  alt="Screenshot"
                  className="mx-auto max-h-[70vh] max-w-full object-contain"
                />
              </div>
              {selectedImage.has_pii && (
                <Alert variant="destructive">
                  <AlertTriangle />
                  <AlertDescription>
                    <strong>PII Detected!</strong> Sensitive information is highlighted in the image.
                  </AlertDescription>
                </Alert>
              )}
              <dl className="grid grid-cols-3 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Time</dt>
                <dd className="col-span-2 text-foreground">
                  {dayjs(selectedImage.timestamp).format('MMM DD, YYYY HH:mm:ss')}
                </dd>
                <dt className="text-muted-foreground">App</dt>
                <dd className="col-span-2 text-foreground">{selectedImage.application}</dd>
                <dt className="text-muted-foreground">Window</dt>
                <dd className="col-span-2 text-foreground">{selectedImage.active_window}</dd>
              </dl>
              {selectedImage.has_pii && (
                <Badge variant="destructive">
                  <AlertTriangle className="h-3 w-3" />
                  Contains PII
                </Badge>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>
                Close
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </>
  );
}

export default RecentScreenshots;
